// environmentFiles — where an environment's files are, according to the server.
//
// Every baked artifact used to be found by computing its path from the splat's
// path (strip the filename, append `baked/…`) and then fetching to see whether
// it was there. That coupled the app to the asset-manager's storage layout, and
// when a second layout appeared with the org migration the computed paths were
// simply wrong — silently, because a wrong path and an unbaked environment are
// the same 404 (ENG-999).
//
// So the server states it. `GET /api/org/environments/{id}` returns a `files`
// map of logical name -> URL, and names an artifact ONLY when its object exists.
// This module is the lookup: callers ask for "lightprobes" and get a URL or
// null, and nothing in the app knows what a storage path looks like.
//
// null therefore means "the server says this is not baked" — a real answer, not
// a failure. Callers should skip, not fetch-and-hope.

// No top-level imports on purpose. The API client reaches ./apiClient through an
// extensionless specifier only Vite resolves, so importing it here would make
// this module — and everything that consumes it — unloadable in a node unit
// test. They are pulled in lazily instead, which also means nothing is fetched
// until something actually asks where a file is.
import { isNamespacedAssetId } from './environmentAssetId.js'

let apiOverride = null
let storeOverride = null
let anchorStoreOverride = null

async function api() {
  return apiOverride ?? import('./assetManagerApi.js')
}
async function store() {
  return storeOverride ?? (await import('./liveTuneStore.js')).liveTuneStore
}
async function anchorStore() {
  return anchorStoreOverride ?? (await import('./environmentAnchorStore.js')).environmentAnchorStore
}

/**
 * Test seam for the collaborators this module reaches. Needed because the
 * API client is only resolvable by Vite (an extensionless ./apiClient import),
 * so a node test cannot load the real one — and the lookup/coalescing logic here
 * is worth testing against something.
 */
export function _setCollaboratorsForTests({ api: a, store: s, anchorStore: an } = {}) {
  apiOverride = a ?? null
  storeOverride = s ?? null
  anchorStoreOverride = an ?? null
}

// splatUrl -> Promise<files|null>. Keyed on the splat URL because that is what
// every consumer already has in hand; one in-flight resolve is shared.
const bySplatUrl = new Map()
// splatUrl -> files|null, the RESOLVED value of the promise above — for the
// render-path callers (the Build gate) that need a synchronous read, exactly
// like knownAssetId beside idBySplatUrl. Absent means "not resolved yet",
// which a caller must treat as unknown, never as "the server listed nothing".
const resolvedBySplatUrl = new Map()
// splatUrl -> assetId, for the call sites that need it synchronously.
const idBySplatUrl = new Map()
// splatUrl -> the environment row itself, as the list published it. Filled by
// the same lookup that resolves the id (see lookupAssetId), because the parts
// of a multipart environment (ENG-1132) are on that row and re-listing to read
// them would be a second fetch for data already in hand.
const rowBySplatUrl = new Map()
// splatUrl -> in-flight id lookup. The synchronous readers (the Build actions,
// the lighting chip) call ensureAssetId on every render until an id appears, so
// without this one unresolved environment fires a list fetch per render.
const idInFlight = new Map()

function stripQuery(url) {
  return (url || '').split('?')[0]
}

/**
 * The environment asset id whose file is `splatUrl`, by asking the server which
 * environments exist and matching on the URL it published for each.
 *
 * Matching server-provided values, rather than parsing the URL, is the whole
 * point: it works for both serve-route shapes without knowing either, it works
 * for a `?splat=` deep link that never went through the picker, and it cannot be
 * broken by a future layout.
 */
function resolveAssetId(splatUrl) {
  const target = stripQuery(splatUrl)
  if (idBySplatUrl.has(target)) return Promise.resolve(idBySplatUrl.get(target))
  if (!idInFlight.has(target)) {
    idInFlight.set(target, lookupAssetId(target).finally(() => idInFlight.delete(target)))
  }
  return idInFlight.get(target)
}

async function lookupAssetId(target) {
  const { listOrgEnvironments } = await api()
  const rows = await listOrgEnvironments()
  const hit = (rows ?? []).find(r => stripQuery(r.fileUrl ?? r.file_url) === target)
  const id = hit?.id ?? null
  idBySplatUrl.set(target, id)
  // Keep the ROW too, so the parts lookup below rides the same fetch. Set even
  // when nothing matched (null), so a `?splat=` deep link at a URL no row
  // publishes is answered "this environment has no parts" once, rather than
  // re-listing on every render.
  rowBySplatUrl.set(target, hit ?? null)
  // Publish it, so the call sites that read an id synchronously during render
  // (the Build actions, the lighting chip) get one without re-deriving it. Only
  // for the environment currently loaded, and never overwriting a known id.
  // The publish below is a SIDE EFFECT — it decorates the store for the render-
  // path readers, and the lookup's answer does not depend on it. So it cannot be
  // allowed to fail the lookup: `setStateEphemeral` is a deepMerge plus a
  // SYNCHRONOUS notify(), so any subscriber that throws would propagate out of
  // `environmentFiles()` and land in a consumer's catch — which in the Load
  // Environment node renders as "(couldn't read formats)" on an environment
  // whose formats the server just listed correctly. Reported 2026-08-31 (a grey
  // FORMAT dropdown with the server answering 200 for every format); this is a
  // route to that symptom, not a proven one, and it is a bug either way.
  try {
    publishAssetId(await store(), target, id)
  } catch (err) {
    console.warn(`[environmentFiles] resolved ${id} for ${target} but could not publish it to the store: ${err?.message ?? err}`)
  }
  // Load the row's anchors too: a restored environment skips applyEnvironment,
  // so a reload left none. Must not fail the lookup.
  // See tests/unit/environmentFiles.test.mjs.
  try {
    await loadAnchorsForLiveEnvironment(await store(), target, hit)
  } catch (err) {
    console.warn(`[environmentFiles] resolved ${id} for ${target} but could not load its anchors: ${err?.message ?? err}`)
  }
  return id
}

// Loads the row's anchors if it is the loaded environment (publishAssetId's guard).
async function loadAnchorsForLiveEnvironment(tune, target, row) {
  if (!row) return
  if (stripQuery(tune.getState()?.environment?.splatUrl) !== target) return
  ;(await anchorStore()).load(row)
}

function publishAssetId(tune, target, id) {
  const env = tune.getState()?.environment
  if (id && stripQuery(env?.splatUrl) === target && env?.assetId !== id) {
    // EPHEMERAL, deliberately. This is a value re-derivable from the server, not
    // a setting the user chose, and setStateEphemeral exists precisely so such
    // values don't "masquerade as the dev's own localStorage settings next
    // load". Persisting it also wrote a whole tune snapshot as a side effect,
    // which then won over URL params on the next navigation — that is how a
    // resolved asset id silently disabled `?probe=1`.
    tune.setStateEphemeral({ environment: { assetId: id } })
  }
}

/**
 * State outright that `splatUrl` is a file of environment `assetId`, for the
 * callers that HAVE the row rather than only a URL (applyEnvironment).
 *
 * The URL match above is the FALLBACK, written for the callers that genuinely
 * have no row — a `?splat=` deep link. It cannot answer for every URL: a
 * format-pinned load (ENG-1070) takes its splat URL from this environment's own
 * `files` map, and the two endpoints publish DIFFERENT serve URLs for the same
 * object — the collection's `fileUrl` is org-scoped, the detail's `files` entries
 * are not — so no row's `fileUrl` equals it. The id then resolved null and every
 * baked artifact of a fully baked environment read as absent — reported against
 * Startup_Level, 2026-08-28. Note that is not about which FORMAT was pinned:
 * pinning the encoding the row already serves failed identically, because the
 * deciding fact is which endpoint the URL came from, not which file it names.
 *
 * So the id is CARRIED here too, the same rule activeEnvironmentAsset.js already
 * states for the store. Positive only: "no id for this URL" is a lookup result,
 * and letting a caller record one would let a half-built row disable a real
 * environment.
 *
 * Never cleared on unload, deliberately: a serve URL contains its own asset id,
 * so the pairing is permanently true and cannot go stale the way the store's
 * `assetId` could (see applyEnvironment.js).
 */
export function noteAssetIdForSplatUrl(splatUrl, assetId) {
  const key = stripQuery(splatUrl)
  if (!key || !assetId) return
  // A registry-namespaced id (`environment:<uuid>`) is not an id the server has
  // heard of: the detail endpoint answers 400 and this environment's whole
  // `files` map reads as null — every baked artifact absent, and the NAVMESH
  // panel blaming a missing .ply the server is serving. Callers unwrap it with
  // environmentAssetId(); this says so loudly rather than caching the bad id,
  // because the symptom is indistinguishable from an unbaked environment.
  if (isNamespacedAssetId(assetId)) {
    console.error(`[environmentFiles] refusing a namespaced asset id "${assetId}" for ${key} — pass the server id (environmentAssetId()), not the registry's`)
    return
  }
  if (idBySplatUrl.get(key) === assetId) return
  // A consumer may have asked BEFORE the pairing was stated and cached the miss
  // (or an older id). Drop that files entry so the next ask re-resolves against
  // the right environment — otherwise the correct answer arriving late changes
  // nothing and the environment stays unbaked for the life of the page.
  idBySplatUrl.set(key, assetId)
  bySplatUrl.delete(key)
  resolvedBySplatUrl.delete(key)
}

/**
 * The active environment's asset id if it has already been resolved, else null.
 * Synchronous, for render-path callers; `environmentFiles()` is what resolves it.
 */
export function knownAssetId(splatUrl) {
  return idBySplatUrl.get(stripQuery(splatUrl)) ?? null
}

/**
 * The RESOLVED files map for a splat URL, synchronously — or undefined when it
 * has not resolved yet (unknown, not "nothing"). For render-path callers; pair
 * with a call to `environmentFiles()` somewhere so it eventually resolves.
 */
export function knownEnvironmentFiles(splatUrl) {
  return resolvedBySplatUrl.get(stripQuery(splatUrl))
}

/** Resolve and publish the asset id for a splat URL. Safe to call repeatedly. */
export function ensureAssetId(splatUrl) {
  const key = stripQuery(splatUrl)
  if (!key || idBySplatUrl.has(key)) return Promise.resolve(knownAssetId(key))
  return resolveAssetId(key).catch(err => {
    // Not cached as "no such environment": a failed lookup must not
    // permanently disable the Build actions for a real environment.
    idBySplatUrl.delete(key)
    console.warn(`[environmentFiles] could not resolve an asset id for ${key}: ${err?.message ?? err}`)
    return null
  })
}

/**
 * The `files` map for the environment whose splat is `splatUrl`, or null when it
 * is not a server-side environment asset (a dev/bundled path, or a `?splat=`
 * pointing outside the catalogue). Cached per splat URL.
 */
export function environmentFiles(splatUrl) {
  const key = stripQuery(splatUrl)
  if (!key) return Promise.resolve(null)
  if (!bySplatUrl.has(key)) {
    bySplatUrl.set(key, (async () => {
      const id = await resolveAssetId(key)
      if (!id) {
        resolvedBySplatUrl.set(key, null)
        return null
      }
      const { getOrgEnvironment } = await api()
      const env = await getOrgEnvironment(id)
      const files = env?.files ?? null
      resolvedBySplatUrl.set(key, files)
      return files
    })().catch(err => {
      // Do not cache a failure: a transient server error must not permanently
      // convince the app that an environment has no baked lighting. Clearing the
      // entry lets the next consumer retry.
      bySplatUrl.delete(key)
      throw err
    }))
  }
  return bySplatUrl.get(key)
}

/**
 * The URL for one logical file of an environment, or null when the server does
 * not have it. `name` is a logical name the server defines — "splat",
 * "lightprobes", "reflections", "navmesh", "collision", "voxels".
 *
 * Resolution failures (server unreachable) also yield null: a caller that
 * cannot be told where a file is behaves as it would if the file were absent,
 * and each caller already reports that state loudly in its own terms.
 */
export async function environmentFileUrl(splatUrl, name) {
  try {
    const files = await environmentFiles(splatUrl)
    return files?.[name] ?? null
  } catch (e) {
    console.warn(`[environmentFiles] could not resolve ${name} for ${splatUrl}: ${e?.message ?? e}`)
    return null
  }
}

/**
 * Drop ONLY the files map for the environment with this asset id — the bake
 * completion path. A completed bake changes which artifacts exist, so the
 * cached `files` is stale the moment bakeStatus flips ready; without this the
 * artifact volumes re-fetch against the pre-bake map, find no "navmesh" entry,
 * and a just-built navmesh still reads NOT BUILT. The id cache is deliberately
 * kept: a re-bake never changes the asset id, and dropping it would blank
 * activeEnvAssetId (and with it the reload signal) mid-reload.
 */
export function forgetEnvironmentFilesById(assetId) {
  if (!assetId) return
  for (const [key, id] of idBySplatUrl) {
    if (id === assetId) {
      bySplatUrl.delete(key)
      resolvedBySplatUrl.delete(key)
    }
  }
}

/** Drop the cache (a re-bake changes which artifacts exist). */
export function forgetEnvironmentFiles(splatUrl) {
  if (splatUrl) {
    const key = stripQuery(splatUrl)
    bySplatUrl.delete(key)
    resolvedBySplatUrl.delete(key)
    idBySplatUrl.delete(key)
    rowBySplatUrl.delete(key)
  } else {
    bySplatUrl.clear()
    resolvedBySplatUrl.clear()
    idBySplatUrl.clear()
    rowBySplatUrl.clear()
  }
}

/**
 * The parts of the environment whose file is `splatUrl` (ENG-1132), as the
 * SERVER states them — a list, in the shape liveTuneStore holds.
 *
 * This is the reconciliation path, and it is why parts are never persisted. A
 * reload restores `splatUrl` from localStorage and nothing else, so without
 * asking the server a four-part room would come back as its walls alone: not an
 * error, not an empty state, just a room quietly missing its furniture. Asking
 * makes the server the source of truth on every load, as the UX rules require,
 * and it costs nothing extra — the environment list is already fetched to
 * resolve the asset id.
 *
 * Returns [] for an environment with no parts, and THROWS if the list cannot be
 * fetched. A caller must not turn that into an empty room: "the server is
 * unreachable" and "this environment is one file" are different facts.
 */
export async function environmentPartsFor(splatUrl) {
  if (!splatUrl) return []
  const target = stripQuery(splatUrl)
  if (!rowBySplatUrl.has(target)) await resolveAssetId(splatUrl)
  const row = rowBySplatUrl.get(target)
  if (!row) return []
  const { readParts, partList } = await import('./environmentParts.js')
  return partList(readParts(row).parts)
}

/**
 * Test seam: state what the server would say for a splat URL, so the modules
 * that consume this can be driven without a server. Seeding the cache exercises
 * the same lookup production uses, rather than a parallel test-only path.
 */
export function seedEnvironmentFilesForTests(splatUrl, files, assetId = 'seeded-asset', row = null) {
  const key = stripQuery(splatUrl)
  idBySplatUrl.set(key, assetId)
  rowBySplatUrl.set(key, row)
  bySplatUrl.set(key, Promise.resolve(files))
  resolvedBySplatUrl.set(key, files)
}

if (typeof window !== 'undefined') {
  // Diagnosis seam: what the server said this environment has.
  window.__ENV_FILES__ = { get: environmentFiles, url: environmentFileUrl, forget: forgetEnvironmentFiles }
}
